<template>
  <AiLoginPage
    v-model:authorization-code="authorizationCode"
    :progress="displayProgress"
    :progress-caption="progressCaption"
    :verification-state="verificationState"
    :verification-title="verificationTitle"
    :verification-detail="verificationDetail"
    :verification-state-label="verificationStateLabel"
    :button-label="primaryButtonLabel"
    :button-hint="primaryButtonHint"
    :left-modules="leftModules"
    :right-modules="rightModules"
    :is-submitting="isCheckingPassword"
    :is-leaving="isLeaving"
    :button-ripple-id="buttonRippleId"
    @submit="handleLogin"
  />
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue';
import AiLoginPage from '@/components/ai-login/AiLoginPage.vue';

type VerificationState = 'idle' | 'verifying' | 'verified' | 'error';
type ModuleStatus = 'done' | 'active' | 'pending';

interface ModuleSeed {
  title: string;
  threshold: number;
  side: 'left' | 'right';
}

interface ModuleItem {
  title: string;
  desc: string;
  status: ModuleStatus;
}

const INITIAL_PROGRESS = 0;

export default defineComponent({
  name: 'LoginScreen',
  components: {
    AiLoginPage,
  },
  emits: ['login-success'],
  setup(_, { emit }) {
    const authorizationCode = ref('');
    const verificationState = ref<VerificationState>('idle');
    const progress = ref(INITIAL_PROGRESS);
    const buttonRippleId = ref(0);
    const isCheckingPassword = ref(false);
    const isLeaving = ref(false);

    let progressTimer: number | undefined;
    let progressCompleteTimer: number | undefined;

    const moduleCatalog: ModuleSeed[] = [
      { title: '连接语言引擎', threshold: 20, side: 'left' },
      { title: '知识库检索服务', threshold: 42, side: 'left' },
      { title: '学习空间初始化', threshold: 78, side: 'left' },
      { title: '语音识别模块', threshold: 28, side: 'right' },
      { title: '问答模型加载', threshold: 56, side: 'right' },
      { title: '个性化配置', threshold: 90, side: 'right' },
    ];

    const displayProgress = computed(() =>
      Math.max(0, Math.min(100, Math.round(progress.value)))
    );

    const progressCaption = computed(() => {
      if (verificationState.value === 'verified') return '正在进入系统...';
      if (verificationState.value === 'error') return '等待重新验证';
      if (verificationState.value === 'verifying') return '正在加载中...';
      return '等待授权中';
    });

    const verificationTitle = computed(() => {
      if (verificationState.value === 'verified') {
        return '授权验证通过，正在进入 AI 学习空间';
      }
      if (verificationState.value === 'error') {
        return '授权校验失败，请检查授权码后重试';
      }
      if (verificationState.value === 'verifying') {
        return 'AI 模型、语义索引与配置中心正在同步';
      }
      return '请输入授权码，完成身份校验';
    });

    const verificationDetail = computed(() => {
      if (verificationState.value === 'verified') {
        return '系统资源已就绪，正在打开高阶交互界面';
      }
      if (verificationState.value === 'error') {
        return '授权码无效或网络异常，请重新输入后继续';
      }
      if (verificationState.value === 'verifying') {
        return '当前正在进行安全验证、模块预热与会话初始化';
      }
      return '当前尚未开始登录验证，所有模块将在授权通过后启动';
    });

    const verificationStateLabel = computed(() => {
      if (verificationState.value === 'verified') return '已验证';
      if (verificationState.value === 'error') return '需重试';
      if (verificationState.value === 'verifying') return '验证中';
      return '待验证';
    });

    const primaryButtonLabel = computed(() => {
      if (verificationState.value === 'verified') return '进入完成';
      if (verificationState.value === 'error') return '重新验证';
      if (verificationState.value === 'verifying') return '正在进入';
      return '验证并进入';
    });

    const primaryButtonHint = computed(() => {
      if (verificationState.value === 'verified')
        return '界面正在完成最后阶段加载';
      if (verificationState.value === 'error')
        return '请确认授权码后再次发起校验';
      if (verificationState.value === 'verifying')
        return '请稍候片刻，精彩即将开始';
      return '输入授权码后启动系统验证并进入 AI 智慧教育系统';
    });

    const currentModuleStatus = (threshold: number): ModuleStatus => {
      if (
        verificationState.value === 'idle' ||
        verificationState.value === 'error'
      ) {
        return 'pending';
      }

      if (displayProgress.value >= threshold) return 'done';
      if (displayProgress.value >= threshold - 18) return 'active';
      return 'pending';
    };

    const statusText = (status: ModuleStatus): string => {
      if (status === 'done') return '已完成';
      if (status === 'active') return '进行中';
      return '待启动';
    };

    const buildModules = (side: 'left' | 'right'): ModuleItem[] =>
      moduleCatalog
        .filter((item) => item.side === side)
        .map((item) => {
          const status = currentModuleStatus(item.threshold);
          return {
            title: item.title,
            desc: statusText(status),
            status,
          };
        });

    const leftModules = computed(() => buildModules('left'));
    const rightModules = computed(() => buildModules('right'));

    const clearTimers = (): void => {
      if (progressTimer) {
        window.clearInterval(progressTimer);
        progressTimer = undefined;
      }

      if (progressCompleteTimer) {
        window.clearTimeout(progressCompleteTimer);
        progressCompleteTimer = undefined;
      }
    };

    const resetToIdle = (): void => {
      clearTimers();
      verificationState.value = 'idle';
      progress.value = INITIAL_PROGRESS;
      isCheckingPassword.value = false;
      isLeaving.value = false;
    };

    const animateProgressTo = (
      target: number,
      duration: number
    ): Promise<void> =>
      new Promise((resolve) => {
        const start = progress.value;
        const delta = target - start;
        const startedAt = performance.now();

        const step = (now: number): void => {
          const ratio = Math.min(1, (now - startedAt) / duration);
          const eased = 1 - Math.pow(1 - ratio, 3);
          progress.value = start + delta * eased;
          if (ratio < 1) {
            requestAnimationFrame(step);
            return;
          }
          resolve();
        };

        requestAnimationFrame(step);
      });

    const startProgressLoop = (): void => {
      clearTimers();
      progress.value = Math.max(progress.value, 12);
      progressTimer = window.setInterval(() => {
        if (progress.value < 92) {
          progress.value = Math.min(92, progress.value + 0.9);
        }
      }, 100);
    };

    const enterFullscreen = async (): Promise<void> => {
      const element = document.documentElement as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void> | void;
        msRequestFullscreen?: () => Promise<void> | void;
      };

      try {
        if (element.requestFullscreen) {
          await element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          await Promise.resolve(element.webkitRequestFullscreen());
        } else if (element.msRequestFullscreen) {
          await Promise.resolve(element.msRequestFullscreen());
        }
      } catch {
        // Ignore fullscreen rejection and continue flow.
      }
    };

    const finishSuccess = async (username?: string): Promise<void> => {
      verificationState.value = 'verified';
      clearTimers();
      await animateProgressTo(100, 900);
      isLeaving.value = true;
      progressCompleteTimer = window.setTimeout(() => {
        void enterFullscreen();
        emit('login-success', { username: username || '' });
      }, 620);
    };

    const handleLogin = async (): Promise<void> => {
      if (isCheckingPassword.value) {
        return;
      }

      if (!authorizationCode.value.trim()) {
        verificationState.value = 'error';
        progress.value = INITIAL_PROGRESS;
        return;
      }

      isCheckingPassword.value = true;
      buttonRippleId.value = Date.now();
      verificationState.value = 'verifying';
      startProgressLoop();

      try {
        localStorage.removeItem('backendConfig');
        document.cookie =
          'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

        const response = await fetch('/api/password/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password: authorizationCode.value }),
          credentials: 'include',
        });

        const data = await response.json();

        if (data.valid) {
          authorizationCode.value = '';
          await finishSuccess(data.username || '');
          return;
        }

        verificationState.value = 'error';
        clearTimers();
        await animateProgressTo(18, 220);
      } catch {
        verificationState.value = 'error';
        clearTimers();
        await animateProgressTo(14, 220);
      } finally {
        isCheckingPassword.value = false;
      }
    };

    onBeforeUnmount(() => {
      resetToIdle();
    });

    return {
      authorizationCode,
      buttonRippleId,
      displayProgress,
      handleLogin,
      isLeaving,
      isCheckingPassword,
      leftModules,
      primaryButtonHint,
      primaryButtonLabel,
      progressCaption,
      rightModules,
      verificationDetail,
      verificationState,
      verificationStateLabel,
      verificationTitle,
    };
  },
});
</script>
